import { deviceTimeZone, localDateString, type MealSlot, scaleToGrams } from '@gramas/shared'
import { Button, MacroPreview, NumberField, Pill, Segmented, Sheet } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useState } from 'react'
import { db, type LocalRecipe } from '../../db/dexie.js'
import {
  foodDisplayName,
  foodPer100g,
  liveIngredients,
  logRecipe,
  restoreRecipe,
  SLOT_LABEL,
  SLOT_ORDER,
  slotForHour,
} from '../../db/repo.js'
import { logContext } from '../../sass/context.js'
import { maybeLogToast, sassToast } from '../../sass/toast.js'
import { syncNow } from '../../sync/engine.js'

export interface LogRecipeSheetProps {
  open: boolean
  recipe: LocalRecipe | null
  onOpenChange: (open: boolean) => void
  onDelete: (id: string) => Promise<void>
  /** Opens the editor on this recipe — see the ingredient list below. */
  onEdit?: (recipe: LocalRecipe) => void
}

/** Log a portion of a cooked batch by weighing what actually went on the plate. */
export function LogRecipeSheet({
  open,
  recipe,
  onOpenChange,
  onDelete,
  onEdit,
}: LogRecipeSheetProps) {
  const [slot, setSlot] = useState<MealSlot>(slotForHour())
  const [grams, setGrams] = useState<number | null>(300)

  useEffect(() => {
    if (open && recipe) {
      setSlot(slotForHour())
      setGrams(Math.round(recipe.cookedWeightG / 4))
    }
  }, [open, recipe])

  /**
   * What went into the batch, read back.
   *
   * A saved recipe was write-only: the list was collected once and then had no
   * screen at all, so the obvious next question — how much butter is in this,
   * and can I use less next time — had no answer and no way to act on it. The
   * names come from the phone's own catalogue, which holds them because saving
   * a recipe caches every ingredient it points at.
   */
  const ingredients = useLiveQuery(
    async () => {
      if (!recipe) return []
      const rows = liveIngredients(recipe)
      const foods = await db.foods.bulkGet(rows.map((i) => i.foodId))
      return rows.map((ing, idx) => ({
        id: ing.id,
        name: foods[idx] ? foodDisplayName(foods[idx]) : 'Ištrintas produktas',
        rawWeightG: ing.rawWeightG,
      }))
    },
    [recipe],
    [],
  )

  const preview = useMemo(
    () => (recipe && grams ? scaleToGrams(foodPer100g(recipe), grams) : null),
    [recipe, grams],
  )

  const commit = async () => {
    if (!recipe || !grams) return
    const logDate = localDateString(new Date(), deviceTimeZone())
    await logRecipe(recipe, { slot, quantityG: grams, logDate })
    void syncNow()
    onOpenChange(false)
    maybeLogToast(await logContext(logDate, slot, Math.round(preview?.kcal ?? 0), recipe.name))
  }

  const portions = recipe && grams ? recipe.cookedWeightG / grams : null

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={recipe?.name ?? ''}
      description={recipe ? `${Math.round(recipe.kcal)} kcal / 100 g gatavo` : undefined}
      footer={
        <div className="flex gap-2">
          <Button
            variant="danger"
            // Deletes immediately and offers undo, instead of a confirm()
            // nobody reads. The tombstone is soft, so undo just clears it.
            onClick={() => {
              if (!recipe) return
              const { id, name } = recipe
              void onDelete(id).then(() => {
                sassToast('recipe_deleted', { itemName: name }, `Ištrinta: ${name}`, {
                  label: 'Atšaukti',
                  run: async () => {
                    await restoreRecipe(id)
                    void syncNow()
                  },
                })
              })
            }}
          >
            Ištrinti
          </Button>
          <Button variant="primary" block onClick={() => void commit()} disabled={!grams}>
            Įrašyti {preview ? `${Math.round(preview.kcal)} kcal` : ''}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <Segmented
          label="Valgymas"
          value={slot}
          onValueChange={setSlot}
          className="w-full"
          options={SLOT_ORDER.map((s) => ({ value: s, label: SLOT_LABEL[s] }))}
        />
        <NumberField
          label="Porcijos svoris"
          suffix="g"
          hint={
            portions != null && Number.isFinite(portions)
              ? `Visa partija ≈ ${portions.toFixed(1)} tokių porcijų`
              : undefined
          }
          value={grams}
          onChange={setGrams}
        />
        <div className="flex flex-wrap gap-2">
          {recipe &&
            [2, 3, 4, 6].map((n) => {
              const portion = Math.round(recipe.cookedWeightG / n)
              return (
                <Pill key={n} numeric active={grams === portion} onClick={() => setGrams(portion)}>
                  1/{n} partijos
                </Pill>
              )
            })}
        </div>
        {preview && (
          <MacroPreview
            kcal={preview.kcal}
            proteinG={preview.protein}
            carbsG={preview.carbs}
            fatG={preview.fat}
          />
        )}

        {ingredients.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-dim">Iš ko pagaminta</span>
              {recipe && onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(recipe)}
                  className="text-sm text-accent-text"
                >
                  Keisti
                </button>
              )}
            </div>
            <ul className="rounded-[var(--radius-card)] border border-line">
              {ingredients.map((ing) => (
                <li
                  key={ing.id}
                  className="relative flex items-baseline justify-between gap-3 px-3 py-1.5 after:absolute after:bottom-0 after:left-3 after:right-0 after:h-px after:bg-line last:after:hidden"
                >
                  <span className="min-w-0 truncate text-sm text-ink">{ing.name}</span>
                  <span className="tnum shrink-0 text-sm text-mute">
                    {Math.round(ing.rawWeightG)} g
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-mute">
              Žali svoriai. Pakeitus kiekį, maistingumas perskaičiuojamas visai partijai.
            </p>
          </div>
        )}
      </div>
    </Sheet>
  )
}
